import React from 'react';
import { Button } from '@/components/ui/button';
import { MobileEmailCapture } from './MobileEmailCapture';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { CTA_MESSAGES } from '@/lib/cta-config';

// Story 2.6: Mobile-optimized hero section
export interface MobileHeroProps {
  onGetStarted?: () => void;
  onWatchDemo?: () => void;
}

/**
 * Mobile-first hero section with vertical stacking and touch-optimized controls
 * Adapts content hierarchy for small screens
 */
export const MobileHero: React.FC<MobileHeroProps> = ({
  onGetStarted,
  onWatchDemo,
}) => {
  const { isMobile, isTablet } = useBreakpoint();

  return (
    <div className='w-full'>
      {/* Mobile-optimized content structure */}
      <div className='space-y-6 md:space-y-8'>
        {/* Headline - Mobile typography */}
        <div className='space-y-3 md:space-y-4'>
          <h1
            className={`
            font-black leading-none text-brand-text-primary tracking-tight
            ${
              isMobile
                ? 'text-2xl'
                : isTablet
                  ? 'text-3xl'
                  : 'text-4xl lg:text-5xl xl:text-6xl'
            }
          `}
          >
            <span className='block'>Turn Lottery</span>
            <span className='block text-brand-primary'>Uncertainty Into</span>
            <span className='block'>Guaranteed Profits</span>
          </h1>

          <p
            className={`
            text-brand-text-secondary leading-relaxed font-medium
            ${isMobile ? 'text-base' : 'text-lg lg:text-xl'}
          `}
          >
            {isMobile
              ? 'Stop guessing lottery payments. Get 90% accurate predictions and boost profits by 23%.'
              : 'Revolutionary AI-powered system eliminates lottery payment guesswork. Join 500+ stores increasing profits by 23% with 90% accurate forecasting.'}
          </p>
        </div>

        {/* Mobile-optimized action buttons */}
        <div
          className={`
          flex gap-3
          ${isMobile ? 'flex-col' : 'flex-row'}
        `}
        >
          <Button
            variant='primary'
            size={isMobile ? 'lg' : 'md'}
            onClick={onGetStarted}
            className={`${isMobile ? 'w-full' : ''} relative`}
          >
            <span>{CTA_MESSAGES.trial}</span>
            <span className='absolute -top-1 -right-1 bg-emerald-500 text-white text-xs px-1.5 py-0.5 rounded-full animate-pulse'>
              90% Accuracy
            </span>
            <svg
              className='w-4 h-4'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M13 7l5 5m0 0l-5 5m5-5H6'
              />
            </svg>
          </Button>

          <Button
            variant='outline'
            size={isMobile ? 'lg' : 'md'}
            onClick={onWatchDemo}
            className={isMobile ? 'w-full' : ''}
          >
            <svg
              className='w-4 h-4'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h10M5 7h14l-1 10H6L5 7z'
              />
            </svg>
            <span>{CTA_MESSAGES.demo}</span>
          </Button>
        </div>


        {/* Trust indicators - Mobile condensed */}
        <div className='space-y-4'>
          {/* Key metrics - Mobile layout */}
          <div
            className={`
            grid gap-4 text-center
            ${isMobile ? 'grid-cols-2' : 'grid-cols-3 md:grid-cols-4'}
          `}
          >
            <div className='space-y-1'>
              <div
                className={`
                font-bold text-brand-primary
                ${isMobile ? 'text-lg' : 'text-xl'}
              `}
              >
                90%
              </div>
              <div
                className={`
                text-brand-text-secondary
                ${isMobile ? 'text-xs' : 'text-sm'}
              `}
              >
                Accuracy
              </div>
            </div>

            <div className='space-y-1'>
              <div
                className={`
                font-bold text-brand-success
                ${isMobile ? 'text-lg' : 'text-xl'}
              `}
              >
                23%
              </div>
              <div
                className={`
                text-brand-text-secondary
                ${isMobile ? 'text-xs' : 'text-sm'}
              `}
              >
                Profit Boost
              </div>
            </div>

            {!isMobile && (
              <>
                <div className='space-y-1'>
                  <div
                    className={`
                    font-bold text-brand-accent
                    ${isMobile ? 'text-lg' : 'text-xl'}
                  `}
                  >
                    500+
                  </div>
                  <div
                    className={`
                    text-brand-text-secondary
                    ${isMobile ? 'text-xs' : 'text-sm'}
                  `}
                  >
                    Stores
                  </div>
                </div>

                <div className='space-y-1'>
                  <div
                    className={`
                    font-bold text-brand-warning
                    ${isMobile ? 'text-lg' : 'text-xl'}
                  `}
                  >
                    $2M+
                  </div>
                  <div
                    className={`
                    text-brand-text-secondary
                    ${isMobile ? 'text-xs' : 'text-sm'}
                  `}
                  >
                    Saved
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Social proof - Mobile simplified */}
          <div className='flex items-center justify-center space-x-4 text-sm text-brand-text-secondary'>
            <div className='flex items-center space-x-2'>
              <div className='flex -space-x-1'>
                {[1, 2, 3].map(i => (
                  <div
                    key={i}
                    className='w-6 h-6 rounded-full bg-brand-accent border-2 border-white'
                  />
                ))}
              </div>
              <span className={isMobile ? 'text-xs' : 'text-sm'}>
                Join 500+ stores
              </span>
            </div>

            {!isMobile && (
              <div className='flex items-center space-x-1'>
                <div className='flex space-x-1'>
                  {[1, 2, 3, 4, 5].map(i => (
                    <svg
                      key={i}
                      className='w-4 h-4 text-yellow-400 fill-current'
                      viewBox='0 0 20 20'
                    >
                      <path d='M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z' />
                    </svg>
                  ))}
                </div>
                <span className='text-sm'>4.9/5 rating</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
